import React, { ReactNode } from 'react';

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

import cn from '~/utils/cn';

export interface DropDownMenuItem {
  label: ReactNode;
  onClick?: () => void;
  href?: string;
  className?: string;
  disabled?: boolean;
  icon?: ReactNode;
}

export interface DropDownMenuProps {
  label?: string;
  groups: DropdownMenuGroup[];
  className?: string;
  buttonClassName?: string;
  groupClassName?: string;
  menuClassName?: string;
  itemClassName?: string;
  customMenuButton?: ReactNode;
  anchor?: 'bottom' | 'top';
  align?: 'left' | 'right';
}

export interface DropdownMenuGroup {
  items: DropDownMenuItem[];
}

const Item: React.FC<DropDownMenuItem & { className?: string }> = ({ label, disabled, onClick, icon, className }) => {
  return (
    <MenuItem>
      <button
        className={cn(
          'block w-full cursor-not-allowed px-16 py-8 text-left text-sm text-slate-600 data-focus:outline-hidden',
          { 'text-grey-700 data-focus:bg-grey-100 data-focus:text-grey-900 cursor-pointer': !disabled },
          className
        )}
        disabled={disabled}
        onClick={onClick}
      >
        <div className="inline-flex items-center gap-8">
          {icon ? <div className="h-16 w-16">{icon}</div> : null}
          {label}
        </div>
      </button>
    </MenuItem>
  );
};

const MenuGroup: React.FC<{ group: DropdownMenuGroup; className?: string; itemClassName?: string }> = ({
  group,
  className,
  itemClassName,
}) => (
  <div className={cn('py-2', className)}>
    {group.items.map((item, itemIndex) => (
      <Item key={itemIndex} className={itemClassName} {...item} />
    ))}
  </div>
);

interface MenuButtonProps {
  className?: string;
  label: string;
}

const DefaultMenuButton: React.FC<MenuButtonProps> = ({ className, label }) => {
  return (
    <MenuButton
      className={cn(
        'hover:bg-grey-100 text-grey-900 inline-flex w-full cursor-pointer justify-center gap-x-8 rounded-md bg-white px-16 py-8 text-sm font-semibold shadow-xs',
        className
      )}
    >
      {label}
      <ChevronDownIcon aria-hidden="true" className="text-grey-400 size-5" />
    </MenuButton>
  );
};

const DropdownMenu: React.FC<DropDownMenuProps> = ({
  label = '',
  groups,
  buttonClassName = '',
  groupClassName = '',
  menuClassName = '',
  itemClassName = '',
  customMenuButton,
  anchor = 'bottom',
  align = 'right',
}) => {
  const menuItemsClassName = cn(
    'absolute z-10 w-max divide-y divide-blue-200 rounded-lg bg-white shadow-lg transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in',
    {
      // Vertical positioning
      'mt-2': anchor === 'bottom',
      'mb-2 bottom-full': anchor === 'top',
      // Horizontal positioning
      'right-0': align === 'right',
      'left-0': align === 'left',
      // Transform origin (combination of anchor and align)
      'origin-top-right': anchor === 'bottom' && align === 'right',
      'origin-top-left': anchor === 'bottom' && align === 'left',
      'origin-bottom-right': anchor === 'top' && align === 'right',
      'origin-bottom-left': anchor === 'top' && align === 'left',
    }
  );

  return (
    <Menu as="div" className={cn('relative inline-block text-left', menuClassName)}>
      {customMenuButton ? (
        <MenuButton as="div" className={cn(buttonClassName)}>
          {customMenuButton}
        </MenuButton>
      ) : (
        <DefaultMenuButton className={buttonClassName} label={label} />
      )}

      <MenuItems transition className={menuItemsClassName}>
        {groups.map((group, groupIndex) => (
          <MenuGroup key={groupIndex} group={group} className={groupClassName} itemClassName={itemClassName} />
        ))}
      </MenuItems>
    </Menu>
  );
};

export default DropdownMenu;
